const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'
const tokenKey = 'rdo_token'

export function getApiBase() {
  return apiBase
}

export function getAuthToken() {
  try {
    return window.sessionStorage.getItem(tokenKey) || window.localStorage.getItem(tokenKey) || ''
  } catch {
    return ''
  }
}

export function setAuthToken(token: string, persistent = false) {
  try {
    const offlineMode = window.localStorage.getItem('offlineMode') === '1'
    if (persistent || offlineMode) {
      window.localStorage.setItem(tokenKey, token)
      window.sessionStorage.removeItem(tokenKey)
    } else {
      window.sessionStorage.setItem(tokenKey, token)
      window.localStorage.removeItem(tokenKey)
    }
  } catch {
    // ignore storage errors
  }
}

export function clearAuthToken() {
  try {
    window.localStorage.removeItem(tokenKey)
    window.sessionStorage.removeItem(tokenKey)
  } catch {
    // ignore storage errors
  }
}

// Endpoints whose GET responses are served from IndexedDB when offline
const OFFLINE_CACHED_PATHS = ['/projects', '/submissions', '/accounts', '/roles', '/system-config']

function isOfflineEnabled() {
  return localStorage.getItem('offlineMode') === '1'
}

export async function apiFetch<T>(path: string, options?: RequestInit, useAuth = true): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> | undefined)
  }
  if (useAuth) {
    const token = getAuthToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  const isGet = !options?.method || options.method.toUpperCase() === 'GET'

  try {
    const response = await fetch(`${apiBase}${path}`, {
      ...options,
      headers,
      cache: 'no-store'
    })
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      if (response.status === 401) {
        if (useAuth) {
          window.dispatchEvent(new CustomEvent('auth:unauthorized'))
          throw new Error('Sessão expirada. Faça login novamente.')
        }
        throw new Error(payload.error || 'Credenciais inválidas.')
      }
      if (response.status === 403 && payload.suspended) {
        window.location.replace('/suspended')
        throw new Error('Sistema suspenso.')
      }
      throw new Error(payload.error || 'Erro no servidor')
    }
    const data = (await response.json()) as T

    // Cache successful GET responses for offline use
    if (isGet && isOfflineEnabled() && OFFLINE_CACHED_PATHS.some(p => path.startsWith(p))) {
      import('@/utils/offlineDB').then(({ dbSet }) => dbSet(`api:${path}`, data)).catch(() => null)
    }

    return data
  } catch (err) {
    // When offline and offline mode is enabled, try to serve from IndexedDB cache
    if (!navigator.onLine && isOfflineEnabled() && isGet) {
      const { dbGet } = await import('@/utils/offlineDB')
      // First try the specific path, then try the bundle data
      const cached = await dbGet<T>(`api:${path}`)
      if (cached !== undefined) return cached

      // Fall back to bundle data for key endpoints
      const bundleKey = path.startsWith('/projects') ? 'bundle:projects'
        : path.startsWith('/submissions') ? 'bundle:submissions'
        : path.startsWith('/accounts') ? 'bundle:accounts'
        : path.startsWith('/roles') ? 'bundle:roles'
        : path.startsWith('/system-config') ? 'bundle:systemConfig'
        : null
      if (bundleKey) {
        const bundled = await dbGet<T>(bundleKey)
        if (bundled !== undefined) return bundled
      }
    }
    throw err
  }
}
